import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PendingActionFromAsyncThunk, AnyAsyncThunk } from '@reduxjs/toolkit/dist/matchers';
import { cloneRequest, copyRequest, createRequest, importModuleRequest } from 'core/services/admin';
import { attemptRequest } from 'core/services/product';
import { preview, registerAttempt } from './register';
import { Handles } from 'constants/handles';
import { IHydrate, hydrate } from 'core/actions/product/state';

export interface IOffline {
  id: number;
}

/**
 * Where the counter starts: below every real handle, not at -1.
 *
 * An offline id is sometimes written into a field that also holds a `Handles`
 * value — a branch lane's `COURSE_OUT.sourceHandle` is the id of the course node
 * entity it leaves through. Starting at -1 made the very first id of a session
 * indistinguishable from `Handles.FAIL`, so the link saga saw a real fail route,
 * persisted it immediately, and the lane came back attached to nothing.
 *
 * Every other handle is zero or above, so one below FAIL is enough.
 */
const FIRST_OFFLINE_ID = Handles.FAIL - 1;

export const offlineSlice = createSlice({
  name: 'offline',
  initialState: { id: FIRST_OFFLINE_ID },
  reducers: {
    generateOfflineId: (state, action) => {
      state.id -= action.payload || 1;
    },
  },
  extraReducers: builder => {
    builder.addCase(createRequest.pending, state => {
      state.id = state.id - 1;
    });
    builder.addCase(importModuleRequest.pending, state => {
      state.id = state.id - 1;
    });
    builder.addCase(cloneRequest.pending, state => {
      state.id = state.id - 1;
    });
    builder.addCase(copyRequest.pending, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      if (action.meta.arg?.items) state.id -= action.meta.arg.items.length;
    });
    builder.addCase(attemptRequest.pending, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      state.id = state.id - 1;
    });
    builder.addCase(registerAttempt, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      state.id = state.id - 1;
    });
    builder.addCase(preview, (state, action: PendingActionFromAsyncThunk<AnyAsyncThunk>) => {
      state.id = state.id - 1;
    });
    builder.addCase(hydrate, (state, action: PayloadAction<Partial<IHydrate>>) => {
      if (action.payload.offlineId) state.id = action.payload.offlineId;
    });
  },
});

export const { generateOfflineId } = offlineSlice.actions;

export default offlineSlice.reducer;
